<template>
  <div class="rounded-2xl p-[1px] bg-[#213E52]/20 shadow-xl shadow-black/30 h-full self-stretch w-full min-w-0 overflow-hidden backdrop-blur-md">
    <div class="rounded-2xl bg-gradient-to-b from-[#101832]/20 to-[#080C19]/30 p-3 sm:p-4 md:p-6 h-full flex flex-col min-w-0 glass-panel">
      <div
        class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent
               text-lg sm:text-xl md:text-2xl font-semibold"
        style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
      >
        Vested Tokens
      </div>
      <p class="text-white/50 text-xs sm:text-sm mt-1 mb-3 sm:mb-4"
         style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
        Your vested VRX tokens and claiming status
      </p>

      <div v-if="!account" class="flex-1 flex items-center justify-center py-8">
        <p class="text-slate-400 text-sm">Connect your wallet to view vested tokens</p>
      </div>

      <div v-else-if="hasVesting" class="space-y-3 sm:space-y-4">
        <!-- Stats Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
          <!-- Total Vested -->
          <div class="rounded-xl p-[1px] bg-[#213E52]/60 h-full min-w-0">
            <div class="rounded-xl bg-[#0a1628]/55 px-2 sm:px-3 py-3 sm:py-4 h-full flex flex-col justify-center inner-glass-panel">
              <div class="text-[10px] sm:text-[11px] text-slate-400 mb-1">Total Vested</div>
              <div class="flex items-center gap-1.5">
                <span class="text-[#B8DAFF] text-lg sm:text-xl md:text-2xl font-semibold leading-none tabular-nums">
                  {{ vestedTotalDisplay }}
                </span>
                <img :src="iconVRX" alt="VRX" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain" />
              </div>
            </div>
          </div>

          <!-- Claimed -->
          <div class="rounded-xl p-[1px] bg-[#213E52]/60 h-full min-w-0">
            <div class="rounded-xl bg-[#0a1628]/55 px-2 sm:px-3 py-3 sm:py-4 h-full flex flex-col justify-center inner-glass-panel">
              <div class="text-[10px] sm:text-[11px] text-slate-400 mb-1">Claimed</div>
              <div class="flex items-center gap-1.5">
                <span class="text-emerald-300 text-lg sm:text-xl md:text-2xl font-semibold leading-none tabular-nums">
                  {{ vestedClaimedDisplay }}
                </span>
                <img :src="iconVRX" alt="VRX" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain" />
              </div>
            </div>
          </div>

          <!-- Claimable -->
          <div class="rounded-xl p-[1px] bg-[#213E52]/60 h-full min-w-0">
            <div class="rounded-xl bg-[#0a1628]/55 px-2 sm:px-3 py-3 sm:py-4 h-full flex flex-col justify-center inner-glass-panel">
              <div class="text-[10px] sm:text-[11px] text-slate-400 mb-1">Available Now</div>
              <div class="flex items-center gap-1.5">
                <span class="text-sky-300 text-lg sm:text-xl md:text-2xl font-semibold leading-none tabular-nums">
                  {{ vestedClaimableDisplay }}
                </span>
                <img :src="iconVRX" alt="VRX" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain" />
              </div>
            </div>
          </div>
        </div>

        <!-- Vesting Schedule Info -->
        <div class="rounded-xl p-[1px] bg-[#213E52]/40">
          <div class="rounded-xl bg-[#0a1628]/40 p-3 sm:p-4 space-y-2">
            <div class="flex items-center justify-between text-xs sm:text-sm">
              <span class="text-slate-400">Start Date</span>
              <span class="text-slate-200 font-medium">{{ vestingStartDate }}</span>
            </div>
            <div class="flex items-center justify-between text-xs sm:text-sm">
              <span class="text-slate-400">Duration</span>
              <span class="text-slate-200 font-medium">{{ vestingDuration }}</span>
            </div>
            <div class="flex items-center justify-between text-xs sm:text-sm">
              <span class="text-slate-400">Cliff Period</span>
              <span class="text-slate-200 font-medium">{{ vestingCliff }}</span>
            </div>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="space-y-2">
          <div class="flex items-center justify-between text-xs sm:text-sm">
            <span class="text-slate-400">Vesting Progress</span>
            <span class="text-slate-200 font-semibold">{{ vestingProgress }}%</span>
          </div>
          <div class="h-2 bg-slate-800/50 rounded-full overflow-hidden">
            <div 
              class="h-full bg-gradient-to-r from-sky-500 to-blue-500 transition-all duration-500"
              :style="{ width: vestingProgress + '%' }"
            ></div>
          </div>
        </div>

        <!-- Claim Button -->
        <button
          @click="claimVested"
          :disabled="!canClaim"
          :class="[
            'w-full px-4 py-3 rounded-xl text-white font-semibold transition-all duration-200',
            canClaim
              ? 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 shadow-lg hover:shadow-xl hover:scale-[1.02]'
              : 'bg-slate-700/50 cursor-not-allowed opacity-50'
          ]"
        >
          <span v-if="canClaim">Claim {{ vestedClaimableDisplay }} VRX</span>
          <span v-else>Nothing to Claim Yet</span>
        </button>

        <p class="text-xs text-white/40 text-center">
          Tokens vest linearly over the duration period after the cliff
        </p>
      </div>

      <div v-else class="flex-1 flex items-center justify-center py-8">
        <div class="text-center">
          <div class="text-slate-400 text-sm mb-2">No vested tokens found</div>
          <p class="text-xs text-white/40">Vested tokens will appear here once granted</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import iconVRX from '../assets/VRX.png'

const props = defineProps({
  account: String,
  vestedTotal: [String, Number, Object],
  vestedClaimed: [String, Number, Object],
  vestedClaimable: [String, Number, Object],
  vestedStart: Number,
  vestedDuration: Number,
  vestedCliff: Number,
  fmt18: Function,
  claimVested: Function,
})

const hasVesting = computed(() => {
  if (!props.account) return false
  const total = BigInt(props.vestedTotal ?? 0n)
  return total > 0n
})

const canClaim = computed(() => {
  const claimable = BigInt(props.vestedClaimable ?? 0n)
  return claimable > 0n
})

const vestedTotalDisplay = computed(() => {
  return props.fmt18 ? props.fmt18(props.vestedTotal) : '0'
})

const vestedClaimedDisplay = computed(() => {
  return props.fmt18 ? props.fmt18(props.vestedClaimed) : '0'
})

const vestedClaimableDisplay = computed(() => {
  return props.fmt18 ? props.fmt18(props.vestedClaimable) : '0'
})

const vestingStartDate = computed(() => {
  if (!props.vestedStart || props.vestedStart === 0) return '—'
  const date = new Date(props.vestedStart * 1000)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
})

const vestingDuration = computed(() => {
  if (!props.vestedDuration || props.vestedDuration === 0) return '—'
  const days = Math.floor(props.vestedDuration / 86400)
  return `${days} days`
})

const vestingCliff = computed(() => {
  if (!props.vestedCliff || props.vestedCliff === 0) return 'None'
  const days = Math.floor(props.vestedCliff / 86400)
  return `${days} days`
})

const vestingProgress = computed(() => {
  if (!props.vestedStart || !props.vestedDuration || props.vestedDuration === 0) return 0
  const now = Math.floor(Date.now() / 1000)
  const start = props.vestedStart
  const end = start + props.vestedDuration
  
  if (now < start) return 0
  if (now >= end) return 100
  
  const elapsed = now - start
  const progress = (elapsed / props.vestedDuration) * 100
  return Math.min(100, Math.max(0, Math.round(progress)))
})
</script>

<style scoped>
/* Glass Panel Effect */
.glass-panel {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.inner-glass-panel {
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
</style>

